import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';

import { getAdminTenantsSummaryFilterQuery } from '../../tenant/components/query';
import { CommandProviderState } from '../types';
import { useProvisionAndNavigate } from '../useProvisionAndNavigate';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const RESULT_LIMIT = 8;

const uuidSchema = z.string().uuid();

const buildFilter = (raw: string) => {
  const trimmed = raw.trim();
  const match = uuidSchema.safeParse(trimmed).success
    ? { id: { _eq: trimmed } }
    : { name: { _ilike: `%${trimmed}%` } };
  // Match the admin tenants table — hide soft-deleted tenants so they can't
  // be navigated to or provisioned from the palette.
  return { _and: [match, { deletedAt: { _isNull: true } }] };
};

export const useTenantResults = (query: string): CommandProviderState => {
  const trimmed = query.trim();
  // Start empty so the first keystroke also goes through the debounce
  // window — otherwise the initial render fires a query immediately.
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [trimmed]);

  const shouldQuery = debounced.length >= MIN_QUERY_LENGTH;

  const { data, loading } = useQuery(getAdminTenantsSummaryFilterQuery, {
    skip: !shouldQuery,
    context: eluveAdminHasuraContext,
    variables: {
      filter: buildFilter(debounced),
      limit: RESULT_LIMIT,
    },
  });

  const { run } = useProvisionAndNavigate();

  const results = useMemo(() => {
    // Gate on the current trimmed input as well as `shouldQuery` — otherwise
    // edits (clears OR swaps) can show stale results from the previous
    // query until the debounce timer catches up.
    if (
      !shouldQuery ||
      trimmed.length < MIN_QUERY_LENGTH ||
      trimmed !== debounced
    )
      return [];
    const tenants = data?.adminTenants ?? [];
    return tenants.flatMap((tenant) => {
      if (!tenant.id) return [];
      const tenantId = tenant.id;
      const tenantName = tenant.name ?? '(unnamed tenant)';
      return [
        {
          id: `tenant:${tenantId}`,
          label: tenantName,
          icon: 'Building2' as const,
          onSelect: () =>
            run({
              tenantId,
              targetPath: `/admin/organizations/${tenantId}`,
            }),
          confirmation: tenant.isCurrentUserProvisioned
            ? undefined
            : {
                title: "You don't have access to this organization:",
                detail: tenantName,
                confirmLabel: 'Provision and open',
              },
        },
      ];
    });
  }, [data, shouldQuery, trimmed, debounced, run]);

  return { results, loading };
};
